import React, { Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router'

import LazyComponent, { LoadingRoute, LazyLoadingMainComponent } from '../components/LazyComponent'

import { useSession } from '../providers/SessionProvider'

const ApiManagement = React.lazy(() => import('../containers/Organisation/ApiManagement'))

const CreateApi = React.lazy(() => import('../containers/Organisation/ApiManagement/CreateApi'))

const EditApi = React.lazy(() => import('../containers/Organisation/ApiManagement/EditApi'))

const MemberManagement = React.lazy(() => import('../containers/Organisation/MemberManagement'))

const AddMember = React.lazy(() => import('../containers/Organisation/MemberManagement/AddMember'))

const EditMember = React.lazy(() => import('../containers/Organisation/MemberManagement/EditMember'))

const GitHub = React.lazy(() => import('../containers/Organisation/GitHub'))

const Billing = React.lazy(() => import('../containers/Organisation/Billing'))

const Plan = React.lazy(() => import('../containers/Organisation/Plan'))

const Pause = React.lazy(() => import('../containers/Organisation/Billing/Pause'))

const Cancel = React.lazy(() => import('../containers/Organisation/Billing/Cancel'))

const Delete = React.lazy(() => import('../containers/Organisation/Billing/Delete'))

const AuthorisedApplications = React.lazy(() => import('../containers/Organisation/AuthorisedApplications'))

const OrganisationSettings = React.lazy(() => import('../containers/Organisation/OrganisationSettings'))

const OrganisationIndex = React.lazy(() => import('../containers/Organisation'))

const Teams = React.lazy(() => import('../containers/Organisation/Teams'))

const CreateTeam = React.lazy(() => import('../containers/Organisation/Teams/Create'))

const EditTeam = React.lazy(() => import('../containers/Organisation/Teams/Edit'))

const NotAuthorised = React.lazy(() => import('../containers/NotAuthorised'))

const NotFound = React.lazy(() => import('../components/templates/NotFound'))

const Organisation = () => {
  const { orgId } = useParams()

  const { setOrganisation, organisation, can, error } = useSession({ orgId })

  useEffect(() => {
    setOrganisation({ orgId })
  }, [orgId])

  if (error?.type === 'Organisation')
    return (
      <Suspense fallback={<div />}>
        <NotFound id="organisation" />
      </Suspense>
    )

  if (organisation)
    return (
      <Routes>
        <Route
          path="/"
          element={
            <LazyComponent>
              <OrganisationIndex />
            </LazyComponent>
          }
        />
        <Route
          path="teams/:teamId/edit"
          element={
            <LazyLoadingMainComponent>
              {can('updateOrganisations') ? <EditTeam /> : <NotAuthorised />}
            </LazyLoadingMainComponent>
          }
        />
        <Route
          path="teams/new"
          element={
            <LazyLoadingMainComponent>
              {can('updateOrganisations') ? <CreateTeam /> : <NotAuthorised />}
            </LazyLoadingMainComponent>
          }
        />
        <Route
          path="teams"
          element={
            <LazyLoadingMainComponent>
              {can('updateOrganisations') ? <Teams /> : <NotAuthorised />}
            </LazyLoadingMainComponent>
          }
        />
        <Route
          path="settings"
          element={
            <LazyLoadingMainComponent>
              {can('updateOrganisations') ? <OrganisationSettings /> : <NotAuthorised />}
            </LazyLoadingMainComponent>
          }
        />
        <Route
          path="billing/plan"
          element={
            <LazyLoadingMainComponent>{can('readPlan') ? <Plan /> : <NotAuthorised />}</LazyLoadingMainComponent>
          }
        />
        <Route
          path="billing/pause"
          element={
            <LazyLoadingMainComponent>
              {can('createSubscriptionPause') ? <Pause /> : <NotAuthorised />}
            </LazyLoadingMainComponent>
          }
        />
        <Route
          path="billing/cancel/*"
          element={
            <LazyLoadingMainComponent>
              {can('updateOrganisations') ? <Cancel /> : <NotAuthorised />}
            </LazyLoadingMainComponent>
          }
        />
        <Route
          path="billing/delete/*"
          element={
            <LazyLoadingMainComponent>
              {can('updateOrganisations') ? <Delete /> : <NotAuthorised />}
            </LazyLoadingMainComponent>
          }
        />
        <Route
          path="billing/*"
          element={
            <LazyLoadingMainComponent>{can('readPlan') ? <Billing /> : <NotAuthorised />}</LazyLoadingMainComponent>
          }
        />
        <Route
          caseSensitive
          path="api"
          element={
            <LazyLoadingMainComponent>
              {can('updateOrganisations') ? <ApiManagement /> : <NotAuthorised />}
            </LazyLoadingMainComponent>
          }
        />
        <Route
          path="api/new"
          element={
            <LazyLoadingMainComponent>
              {can('updateOrganisations') ? <CreateApi /> : <NotAuthorised />}
            </LazyLoadingMainComponent>
          }
        />
        <Route
          caseSensitive
          path="api/:uuid/edit"
          element={
            <LazyLoadingMainComponent>
              {can('updateOrganisations') ? <EditApi /> : <NotAuthorised />}
            </LazyLoadingMainComponent>
          }
        />
        <Route
          caseSensitive
          path="members"
          element={
            <LazyLoadingMainComponent>
              {can('updateOrganisations') ? <MemberManagement /> : <NotAuthorised />}
            </LazyLoadingMainComponent>
          }
        />
        <Route
          caseSensitive
          path="members/new"
          element={
            <LazyLoadingMainComponent>
              {can('updateOrganisations') ? <AddMember /> : <NotAuthorised />}
            </LazyLoadingMainComponent>
          }
        />
        <Route
          caseSensitive
          path="members/:uuid/edit"
          element={
            <LazyLoadingMainComponent>
              {can('updateOrganisations') ? <EditMember /> : <NotAuthorised />}
            </LazyLoadingMainComponent>
          }
        />
        <Route
          path="github"
          element={
            <LazyLoadingMainComponent>
              <GitHub />
            </LazyLoadingMainComponent>
          }
        />
        <Route
          path="integrations"
          element={
            <LazyLoadingMainComponent>
              {can('updateOrganisations') ? <AuthorisedApplications /> : <NotAuthorised />}
            </LazyLoadingMainComponent>
          }
        />
      </Routes>
    )

  return <LoadingRoute />
}

export default Organisation
